import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Clock3, Radio, RefreshCw } from 'lucide-react';
import { useApi, type BookingReassignmentAvailabilityStatus } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { StaleNote } from '../detail-view/StaleNote';
import { availabilityStatusLabel } from './reassignmentPresentation';

export function BookingReassignmentHistory({
  bookingId,
  propertyNames,
}: {
  bookingId: string;
  propertyNames: Record<string, string>;
}) {
  const api = useApi();
  const query = useQuery({
    queryKey: ['bookings', 'reassignments', bookingId],
    queryFn: () => api.bookings.reassignmentHistory(bookingId),
    refetchInterval: ({ state }) =>
      state.data?.some((item) => item.availabilityStatus === 'Pending') ? 3_000 : false,
  });
  if (query.isLoading)
    return <p className="text-ink-soft text-sm">Apartmentwechsel werden geladen …</p>;
  // Ein Ladefehler sah bisher aus wie „nie gewechselt“.
  if (query.isError && !query.data) {
    return (
      <p
        role="alert"
        className="border-hairline rounded-tile flex flex-wrap items-center gap-3 border bg-white p-4 text-sm"
      >
        <span className="min-w-0 flex-1">Die Apartmentwechsel konnten nicht geladen werden.</span>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="text-forest min-h-11 font-bold"
        >
          Erneut laden
        </button>
      </p>
    );
  }
  if (!query.data?.length) {
    return (
      <p className="text-ink-soft border-hairline rounded-tile border bg-white p-5 text-sm">
        Kein Apartmentwechsel bei dieser Buchung.
      </p>
    );
  }

  return (
    <section
      className="border-hairline rounded-tile border bg-white p-5"
      aria-labelledby="reassignment-history"
    >
      <h2 id="reassignment-history" className="font-serif text-lg font-bold">
        Apartmentwechsel
      </h2>
      {query.isError && <StaleNote onRetry={() => void query.refetch()} />}
      <div className="mt-4 space-y-3">
        {query.data.map((operation) => {
          const entry = operation.entries.find((item) => item.bookingId === bookingId);
          if (!entry) return null;
          return (
            <article
              key={operation.operationId}
              className="border-hairline rounded-control border p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold">
                    {operation.kind === 'Swap' ? 'Apartmenttausch' : 'Apartment verschoben'}
                  </p>
                  <p className="text-ink-soft mt-1 text-sm">
                    {propertyNames[entry.fromPropertyId] ?? 'Früheres Apartment'}
                    <span aria-hidden="true"> → </span>
                    {propertyNames[entry.toPropertyId] ?? 'Neues Apartment'}
                  </p>
                </div>
                <AvailabilityStatus status={operation.availabilityStatus} />
              </div>
              <div className="text-ink-soft mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                <span>
                  {operation.actorDisplayName ?? `Benutzer ${operation.actorUserId.slice(0, 8)}`}
                </span>
                <span className="flex items-center gap-1">
                  <Clock3 size={13} aria-hidden="true" />
                  {new Intl.DateTimeFormat('de-DE', {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  }).format(new Date(operation.createdAt))}
                </span>
              </div>
              {operation.reason && (
                <p className="bg-paper rounded-control mt-3 p-3 text-sm">{operation.reason}</p>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}

function AvailabilityStatus({ status }: { status: BookingReassignmentAvailabilityStatus }) {
  const config = {
    NotRequired: { icon: Radio, className: 'text-ink-soft' },
    Pending: { icon: RefreshCw, className: 'text-amber-ink' },
    Confirmed: { icon: CheckCircle2, className: 'text-forest' },
    Failed: { icon: AlertTriangle, className: 'text-sealwax' },
  }[status];
  const Icon = config.icon;
  return (
    <span className={cn('flex items-center gap-1.5 text-xs font-semibold', config.className)}>
      <Icon
        size={14}
        className={status === 'Pending' ? 'animate-spin' : undefined}
        aria-hidden="true"
      />
      {availabilityStatusLabel(status)}
    </span>
  );
}
